import { ChevronDownIcon } from '@radix-ui/react-icons';
import { cn } from '@/lib/utils';
import {
  createContext,
  ReactNode,
  useContext,
  useState,
  forwardRef,
  CSSProperties,
} from 'react';
import { motion, AnimatePresence } from 'motion/react';

export function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col space-y-4 rounded-lg border p-4', className)}
    >
      {children}
    </div>
  );
}

const SectionContext = createContext<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}>({ isOpen: false, setIsOpen: () => {} });

function useSectionContext() {
  return useContext(SectionContext);
}

export function Section({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <SectionContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="flex flex-col space-y-2 border-b last:border-b-0">
        {children}
      </div>
    </SectionContext.Provider>
  );
}

export function SectionHeading({
  children,
  collapsible = true,
}: {
  children: ReactNode;
  collapsible?: boolean;
}) {
  const { isOpen, setIsOpen } = useSectionContext();

  return (
    <div
      onClick={() => collapsible && setIsOpen(!isOpen)}
      className={cn(
        '-mx-1 flex items-center justify-between gap-2 rounded-t px-1 text-sm font-bold',
        {
          'cursor-pointer hover:bg-accent': collapsible,
        },
      )}
    >
      <h3 className="flex-1">{children}</h3>
      {collapsible && (
        <motion.div
          animate={{ rotate: isOpen ? 0 : -540 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          <ChevronDownIcon className="h-6 w-6" />
        </motion.div>
      )}
    </div>
  );
}

export function SectionContent({ children }: { children: ReactNode }) {
  const { isOpen } = useSectionContext();
  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'anticipate' }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function SettingRowHeading({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'whitespace-nowrap indent-0 md:w-1/4 md:whitespace-normal md:py-8',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SettingRowDescription({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('text-xs md:w-1/3 md:py-8', className)}>{children}</div>
  );
}

export function SettingRowInput({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('w-full md:w-1/2 md:py-8', className)}>{children}</div>
  );
}

export function SettingRowMultiInput({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('w-full', className)}>{children}</div>;
}

export function SettingRowCell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('md:py-8', className)}>{children}</div>;
}

export function SettingsTable({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-4 md:gap-2', className)}>
      {children}
    </div>
  );
}

export const SettingsRow = forwardRef<
  HTMLTableRowElement,
  {
    children: ReactNode;
    style?: CSSProperties;
    forceColumn?: boolean;
    forceRow?: boolean;
  }
>(({ children, style, forceColumn, forceRow }, ref) => {
  return (
    <div
      className={cn(
        'flex flex-col items-start justify-between gap-2 border-b border-input pb-4 last:border-b-0 md:pb-0',
        forceColumn ? '' : 'md:flex-row md:gap-2',
        forceRow ? 'flex-row items-center' : 'flex-col',
      )}
      ref={ref}
      style={style}
    >
      {children}
    </div>
  );
});
